import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { ref, deleteObject, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../config/firebase';
import { Vehicle, MaintenanceRecord, FuelRecord, VehicleAlert, MileageReport, VehicleStatus, VehicleReservationStatus, VehicleDocument, VehicleUsageType } from '../types/vehicle';
import { MaintenancePlan } from '../types/maintenance';
import { notificationService } from './notificationService';
import { organizationService } from './organizationService';
import { maintenancePlanService } from './maintenancePlanService';
import { userService } from './userService';
import { getDefaultMaintenancePlan } from '../utils/maintenanceUtils';

const VEHICLES_COLLECTION = 'vehicles';

interface GetVehiclesOptions {
  page?: number;
  limit?: number;
  status?: string;
  usageType?: VehicleUsageType | null;
  fleetId?: string | null;
}

interface GetVehiclesResult {
  vehicles: Vehicle[];
  total: number;
}

const getVehicles = async (organizationId: string): Promise<Vehicle[]> => {
  const result = await getOrganizationVehicles(organizationId, {});
  return result.vehicles;
};

const getAssignedVehicle = async (organizationId: string, userId: string): Promise<Vehicle | null> => {
  try {
    console.log('Fetching assigned vehicle for:', { organizationId, userId });
    
    // Récupérer tous les véhicules de l'organisation
    const q = query(
      collection(db, VEHICLES_COLLECTION),
      where('organizationId', '==', organizationId)
    );
    
    const querySnapshot = await getDocs(q);
    console.log('Found vehicles:', querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    
    // Filtrer pour trouver celui qui est assigné à l'utilisateur
    const assignedVehicle = querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as Vehicle))
      .find(vehicle => 
        vehicle.currentAssignment && 
        vehicle.currentAssignment.userId === userId
      );
    
    console.log('Filtered assigned vehicle:', assignedVehicle);
    return assignedVehicle || null;
  } catch (error) {
    console.error('Error getting assigned vehicle:', error);
    throw error;
  }
};

const getOrganizationVehicles = async (
  organizationId: string,
  options: GetVehiclesOptions = {}
): Promise<GetVehiclesResult> => {
  try {
    const { page = 0, limit = 10, status, usageType } = options;
    let q = query(
      collection(db, VEHICLES_COLLECTION),
      where('organizationId', '==', organizationId)
    );

    if (status) {
      q = query(q, where('status', '==', status));
    }

    if (usageType) {
      q = query(q, where('usageType', '==', usageType));
    }

    if (options.fleetId) {
      q = query(q, where('fleetId', '==', options.fleetId));
    }

    q = query(q, orderBy('make', 'asc'));

    const querySnapshot = await getDocs(q);
    const total = querySnapshot.docs.length;
    
    // Apply pagination
    const start = page * limit;
    const paginatedDocs = querySnapshot.docs.slice(start, start + limit);

    const vehicles = paginatedDocs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        lastMileageUpdate: data.lastMileageUpdate || undefined,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        technicalInspection: {
          ...data.technicalInspection,
          lastInspectionDate: data.technicalInspection?.lastInspectionDate || Timestamp.now(),
          nextInspectionDate: data.technicalInspection?.nextInspectionDate || Timestamp.now(),
        }
      } as Vehicle;
    });

    return { vehicles, total };
  } catch (error) {
    console.error('Error getting vehicles:', error);
    throw error;
  }
};

const getVehicle = async (id: string): Promise<Vehicle | null> => {
  try {
    const docRef = doc(db, VEHICLES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Vehicle;
    }
    return null;
  } catch (error) {
    console.error('Error getting vehicle:', error);
    throw error;
  }
};

const createVehicle = async (
  organizationId: string,
  data: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'createdBy'>,
  createdBy: string
): Promise<string> => {
  try {
    // Ensure all required fields have default values
    const vehicleData = {
      ...{
        status: 'active',
        currentMileage: 0,
        totalRepairCosts: 0,
        totalFuelCosts: 0,
        annualMaintenanceCost: 0,
        annualDepreciation: 0,
        enginePower: 0,
        doors: 0,
        seats: 0,
        transmission: 'manual',
        documents: [],
        insurance: {
          provider: '',
          policyNumber: '',
          expirationDate: Timestamp.now()
        },
        technicalInspection: {
          lastInspectionDate: Timestamp.now(),
          nextInspectionDate: Timestamp.now(),
          status: 'valid',
          documents: [],
          history: []
        },
      },
      ...data,
      // Ces champs doivent toujours être définis après data pour ne pas être écrasés
      organizationId,
      createdBy,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    // Clean technicalDetails to remove undefined values
    if (vehicleData.technicalDetails) {
      const cleanedTechnicalDetails = Object.entries(vehicleData.technicalDetails).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      // Only set technicalDetails if there are valid fields, otherwise omit it
      if (Object.keys(cleanedTechnicalDetails).length > 0) {
        vehicleData.technicalDetails = cleanedTechnicalDetails as any;
      } else {
        delete (vehicleData as any).technicalDetails;
      }
    }

    const docRef = await addDoc(collection(db, VEHICLES_COLLECTION), vehicleData);
    const vehicleId = docRef.id;

    // Créer automatiquement un plan de maintenance basé sur le type de carburant
    try {
      // Créer le plan de maintenance avec les paramètres par défaut
      const defaultPlan = getDefaultMaintenancePlan(
        vehicleId,
        organizationId,
        vehicleData.fuelType,
        vehicleData.make,
        vehicleData.model,
        vehicleData.year,
        createdBy
      ) as Omit<MaintenancePlan, 'id' | 'createdAt' | 'updatedAt'>;

      // Créer le plan avec les tâches
      await maintenancePlanService.createCustomPlan(defaultPlan);

      // Notifier l'utilisateur de la création du plan
      const admins = await organizationService.getOrganizationAdmins(organizationId);
      for (const admin of admins) {
        await notificationService.sendNotificationEmail({
          to_email: admin.email,
          to_name: admin.displayName || admin.email,
          subject: `Plan de maintenance créé - ${vehicleData.make} ${vehicleData.model}`,
          message: `Un plan de maintenance a été automatiquement créé pour le véhicule ${vehicleData.make} ${vehicleData.model} (${vehicleData.licensePlate}) basé sur son type de carburant (${vehicleData.fuelType}). Vous pouvez ajuster les paramètres dans la section maintenance du véhicule.`
        });
      }
    } catch (error) {
      console.error('Erreur lors de la création du plan de maintenance:', error);
      // Ne pas bloquer la création du véhicule si la création du plan échoue
    }

    return vehicleId;
  } catch (error) {
    console.error('Error creating vehicle:', error);
    throw error;
  }
};

const updateVehicle = async (id: string, data: Partial<Vehicle>): Promise<void> => {
  try {
    const docRef = doc(db, VEHICLES_COLLECTION, id);
    
    // Remove undefined fields to prevent Firestore errors
    const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    // If currentMileage is being updated, ensure lastMileageUpdate is set
    if ('currentMileage' in cleanData) {
      cleanData.lastMileageUpdate = Timestamp.now();
    }

    await updateDoc(docRef, {
      ...cleanData,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating vehicle:', error);
    throw error;
  }
};

const deleteVehicle = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, VEHICLES_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting vehicle:', error);
    throw error;
  }
};

const getMaintenanceRecords = async (vehicleId: string): Promise<MaintenanceRecord[]> => {
  try {
    const q = query(
      collection(db, 'maintenanceRecords'),
      where('vehicleId', '==', vehicleId),
      orderBy('date', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MaintenanceRecord));
  } catch (error) {
    console.error('Error getting maintenance records:', error);
    throw error;
  }
};

const getFuelRecords = async (vehicleId: string): Promise<FuelRecord[]> => {
  try {
    const q = query(
      collection(db, 'fuelConsumptions'),
      where('vehicleId', '==', vehicleId),
      orderBy('date', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FuelRecord));
  } catch (error) {
    console.error('Error getting fuel records:', error);
    throw error;
  }
};

const getVehicleAlerts = async (vehicleId: string): Promise<VehicleAlert[]> => {
  try {
    const q = query(
      collection(db, 'vehicleAlerts'),
      where('vehicleId', '==', vehicleId),
      orderBy('dueDate', 'asc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VehicleAlert));
  } catch (error) {
    console.error('Error getting vehicle alerts:', error);
    throw error;
  }
};

const getMileageReports = async (vehicleId: string): Promise<MileageReport[]> => {
  try {
    const q = query(
      collection(db, 'mileageReports'),
      where('vehicleId', '==', vehicleId),
      orderBy('date', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MileageReport));
  } catch (error) {
    console.error('Error getting mileage reports:', error);
    throw error;
  }
};

const deleteDocument = async (documentUrl: string): Promise<void> => {
  try {
    // Delete from Firebase Storage
    const storageRef = ref(storage, documentUrl);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

const assignVehicle = async (vehicleId: string, userId: string): Promise<void> => {
  try {
    console.log('Starting vehicle assignment:', { vehicleId, userId });
    
    // Vérifier que le véhicule existe
    const vehicleRef = doc(db, VEHICLES_COLLECTION, vehicleId);
    const vehicleDoc = await getDoc(vehicleRef);
    
    if (!vehicleDoc.exists()) {
      console.error('Vehicle not found:', vehicleId);
      throw new Error('Vehicle not found');
    }

    const vehicleData = vehicleDoc.data();
    console.log('Vehicle found:', vehicleData);

    // Vérifier que le véhicule n'est pas déjà assigné
    if (vehicleData.currentAssignment) {
      console.error('Vehicle already assigned:', vehicleData.currentAssignment);
      throw new Error('Vehicle already assigned');
    }

    const assignmentData = {
      currentAssignment: {
        userId,
        assignedDate: Timestamp.now()
      },
      updatedAt: Timestamp.now(),
    };
    console.log('Assignment data:', assignmentData);

    // Mettre à jour l'assignation
    await updateDoc(vehicleRef, assignmentData);
    console.log('Vehicle assignment updated successfully');

    // Vérifier que l'assignation a bien été faite
    const updatedDoc = await getDoc(vehicleRef);
    const updatedData = updatedDoc.data();
    console.log('Updated vehicle data:', updatedData);

    // Vérifier que l'assignation est correcte
    if (!updatedData?.currentAssignment || updatedData.currentAssignment.userId !== userId) {
      console.error('Assignment verification failed:', updatedData?.currentAssignment);
      throw new Error('Failed to verify vehicle assignment');
    }
  } catch (error) {
    console.error('Error assigning vehicle:', error);
    throw error;
  }
};

const unassignVehicle = async (vehicleId: string): Promise<void> => {
  try {
    const docRef = doc(db, VEHICLES_COLLECTION, vehicleId);
    await updateDoc(docRef, {
      currentAssignment: null,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error unassigning vehicle:', error);
    throw error;
  }
};

const uploadDocument = async (
  vehicleId: string,
  organizationId: string,
  file: File,
  type: 'insurance' | 'registration' | 'maintenance' | 'other'
): Promise<VehicleDocument> => {
  try {
    const fileName = `${type}/${organizationId}/${vehicleId}/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, fileName);
    const metadata = {
      customMetadata: {
        uploadedBy: auth.currentUser?.uid || '',
        vehicleId,
        organizationId
      }
    };
    await uploadBytes(storageRef, file, metadata);
    const url = await getDownloadURL(storageRef);

    return {
      name: file.name,
      type,
      url,
      uploadedAt: Timestamp.now(),
    };
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

interface MileageReportData {
  mileage: number;
  date?: Timestamp;
  notes?: string;
  imageUrl?: string | null;
  reportedBy: string;
  aiVerified?: boolean;
  aiConfidence?: number;
  originalAiReading?: number;
  validationStatus?: 'pending' | 'validated' | 'rejected';
  validationMessage?: string;
}

const addMileageReport = async (
  vehicleId: string,
  data: MileageReportData
): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, 'mileageReports'), {
      vehicleId,
      ...data,
      date: data.date || Timestamp.now(),
    });

    try {
      // Récupérer les informations du véhicule
      const vehicle = await getVehicle(vehicleId);
      
      if (vehicle) {
        // Récupérer les admins de l'organisation
        const admins = await organizationService.getOrganizationAdmins(vehicle.organizationId);

        // Envoyer une notification à chaque admin
        for (const admin of admins) {
          // Notification email
          await notificationService.sendNotificationEmail({
            to_email: admin.email,
            to_name: admin.displayName || admin.email,
            subject: `Nouveau relevé kilométrique - ${vehicle.make} ${vehicle.model}`,
            message: notificationService.formatMileageMessage({
              ...data,
              id: docRef.id,
              vehicleId,
              date: data.date || new Date()
            }, vehicle)
          });

          // Récupérer l'utilisateur par email pour les préférences
          const user = await userService.findUserByEmail(admin.email);
          if (!user?.id) continue;

          // Notification push si activée
          const userPrefs = await notificationService.getNotificationPreferences(user.id);
          if (userPrefs.enabled && userPrefs.mileage) {
            await notificationService.showNotification(
              `Nouveau relevé kilométrique - ${vehicle.make} ${vehicle.model}`,
              {
                body: `Kilométrage: ${data.mileage}km\nDate: ${new Date(data.date?.toDate() || new Date()).toLocaleDateString()}`,
                icon: '/pwa-192x192.png',
                data: {
                  url: `/admin/vehicles/${vehicleId}`
                }
              }
            );
          }
        }
      }
    } catch (notifError) {
      // Log l'erreur mais ne pas bloquer l'ajout du relevé kilométrique
      console.error('Error sending mileage report notification:', notifError);
    }

    return docRef.id;
  } catch (error) {
    console.error('Error adding mileage report:', error);
    throw error;
  }
};

const getVehiclesByOrganization = async (organizationId: string, status?: VehicleStatus): Promise<Vehicle[]> => {
  try {
    let q = query(
      collection(db, VEHICLES_COLLECTION),
      where('organizationId', '==', organizationId)
    );

    if (status) {
      q = query(q, where('status', '==', status));
    }

    q = query(q, orderBy('make', 'asc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Vehicle));
  } catch (error) {
    console.error('Error getting vehicles by organization:', error);
    throw error;
  }
};

const getUnassignedVehicles = async (organizationId: string): Promise<Vehicle[]> => {
  try {
    console.log('Getting unassigned vehicles for org:', organizationId);
    // On ne peut pas utiliser where('currentAssignment', '==', null) directement
    // On récupère tous les véhicules actifs et on filtre côté client
    const q = query(
      collection(db, VEHICLES_COLLECTION),
      where('organizationId', '==', organizationId),
      where('status', '==', 'active')
    );
    
    const querySnapshot = await getDocs(q);
    const vehicles = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Vehicle));

    // Filtrer les véhicules qui n'ont pas d'assignation
    console.log('Found vehicles:', vehicles);
    const unassignedVehicles = vehicles.filter(v => !v.currentAssignment);
    console.log('Unassigned vehicles:', unassignedVehicles);
    return unassignedVehicles;
  } catch (error) {
    console.error('Error getting unassigned vehicles:', error);
    throw error;
  }
};

const assignVehicleToFleet = async (vehicleId: string, fleetId: string | null): Promise<void> => {
  try {
    const docRef = doc(db, VEHICLES_COLLECTION, vehicleId);
    await updateDoc(docRef, {
      fleetId,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error assigning vehicle to fleet:', error);
    throw error;
  }
};

const getVehiclesByFleet = async (fleetId: string): Promise<Vehicle[]> => {
  try {
    const q = query(
      collection(db, VEHICLES_COLLECTION),
      where('fleetId', '==', fleetId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Vehicle));
  } catch (error) {
    console.error('Error getting vehicles by fleet:', error);
    throw error;
  }
};

// Nouvelles méthodes pour la réservation
const updateVehicleReservationStatus = async (
  id: string,
  status: VehicleReservationStatus
): Promise<void> => {
  try {
    const docRef = doc(db, VEHICLES_COLLECTION, id);
    await updateDoc(docRef, {
      reservationStatus: status,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating vehicle reservation status:', error);
    throw error;
  }
};

export const vehicleService = {
  getVehicles,
  getVehicle,
  createVehicle,
  updateVehicle,
  deleteVehicle,
  getOrganizationVehicles,
  getMaintenanceRecords,
  getFuelRecords,
  getVehicleAlerts,
  getMileageReports,
  deleteDocument,
  assignVehicle,
  unassignVehicle,
  uploadDocument,
  addMileageReport,
  getVehiclesByOrganization,
  getAssignedVehicle,
  getUnassignedVehicles,
  assignVehicleToFleet,
  getVehiclesByFleet,
  // Nouvelles méthodes pour la réservation
  updateVehicleReservationStatus,
};

export default vehicleService;
